/**
 * Notification Settings Component
 * Allows users to manage push notification preferences
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Bell, 
  BellOff, 
  BellRing,
  Moon,
  MessageSquare,
  Calendar,
  Video,
  Megaphone,
  Mail,
  Users,
  Target,
  Loader2,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import usePushNotifications from '@/hooks/usePushNotifications';

const NOTIFICATION_TYPES = [
  { 
    key: 'circle_post', 
    label: 'Sister Circle Posts', 
    description: 'New posts in your circles',
    icon: Users 
  },
  { 
    key: 'circle_comment', 
    label: 'Circle Comments', 
    description: 'Replies to your posts',
    icon: MessageSquare 
  },
  { 
    key: 'session_reminder', 
    label: 'Session Reminders', 
    description: 'Upcoming coaching sessions',
    icon: Calendar 
  },
  { 
    key: 'new_content', 
    label: 'New Content', 
    description: 'Videos, resources & materials',
    icon: Video 
  },
  { 
    key: 'admin_broadcast', 
    label: 'Announcements', 
    description: 'Important updates from admin',
    icon: Megaphone 
  },
  { 
    key: 'direct_message', 
    label: 'Direct Messages', 
    description: 'Messages from coaches',
    icon: Mail 
  },
  { 
    key: 'accountability', 
    label: 'Accountability Updates', 
    description: 'Progress & check-ins',
    icon: Target 
  }
];

const NotificationSettings = () => {
  const {
    isSupported,
    isSubscribed,
    isLoading,
    permission,
    preferences,
    subscribe,
    unsubscribe,
    updatePreferences,
    sendTestNotification
  } = usePushNotifications();

  const [localPrefs, setLocalPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [autoPrompted, setAutoPrompted] = useState(false);

  useEffect(() => {
    if (preferences) {
      setLocalPrefs(preferences);
    }
  }, [preferences]);

  // Auto-prompt for notification permission if not already subscribed and permission not denied
  useEffect(() => {
    const autoEnableNotifications = async () => {
      if (
        isSupported && 
        !isSubscribed && 
        !isLoading && 
        !autoPrompted &&
        permission === 'default' // Only prompt if user hasn't made a choice yet
      ) {
        setAutoPrompted(true);
        try {
          await subscribe();
          toast.success('Push notifications enabled! You\'ll receive updates here.');
        } catch (error) {
          // User denied or error - that's okay, they can enable later
          console.log('Auto-subscribe not completed:', error.message);
        }
      }
    };
    
    autoEnableNotifications();
  }, [isSupported, isSubscribed, isLoading, permission, autoPrompted, subscribe]);

  const handleToggleNotifications = async () => {
    try {
      if (isSubscribed) {
        await unsubscribe();
        toast.success('Push notifications disabled');
      } else {
        await subscribe();
        toast.success('Push notifications enabled!');
      }
    } catch (error) {
      if (error.message === 'Notification permission denied') {
        toast.error('Please allow notifications in your browser settings');
      } else {
        toast.error('Failed to update notification settings');
      }
    }
  };

  const handleUpdatePreference = async (key, value) => {
    setLocalPrefs(prev => ({
      ...prev,
      types: { ...prev?.types, [key]: value }
    }));
  };

  const handleToggleQuietHours = (enabled) => {
    setLocalPrefs(prev => ({
      ...prev,
      quiet_hours: { ...prev?.quiet_hours, enabled }
    }));
  };

  const handleQuietHoursTime = (field, value) => {
    setLocalPrefs(prev => ({
      ...prev,
      quiet_hours: { ...prev?.quiet_hours, [field]: parseInt(value) }
    }));
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      await updatePreferences(localPrefs);
      toast.success('Notification preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleTestNotification = async () => {
    setTesting(true);
    try {
      const result = await sendTestNotification();
      toast.success(result.message);
    } catch (error) {
      toast.error('Failed to send test notification');
    } finally {
      setTesting(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-forest" />
            <span className="ml-2 text-gray-500">Loading notification settings...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!isSupported) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center">
            <BellOff className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Push notifications are not supported in your browser.</p>
            <p className="text-sm text-gray-400 mt-1">Try using Chrome, Firefox, or Edge on desktop.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Toggle Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${isSubscribed ? 'bg-forest/10' : 'bg-gray-100'}`}>
                {isSubscribed ? (
                  <BellRing className="h-6 w-6 text-forest" />
                ) : (
                  <BellOff className="h-6 w-6 text-gray-400" />
                )}
              </div>
              <div>
                <CardTitle className="text-lg">Push Notifications</CardTitle>
                <CardDescription>
                  Get instant updates on your device
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {isSubscribed && (
                <Badge className="bg-green-100 text-green-700">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Enabled
                </Badge>
              )}
              {permission === 'denied' && (
                <Badge className="bg-red-100 text-red-700">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Blocked
                </Badge>
              )}
              <Switch
                checked={isSubscribed}
                onCheckedChange={handleToggleNotifications}
                disabled={permission === 'denied'}
              />
            </div>
          </div>
        </CardHeader>
        
        {permission === 'denied' && (
          <CardContent className="pt-0">
            <div className="p-3 bg-red-50 rounded-lg text-sm text-red-700">
              Notifications are blocked. To enable them, click the lock icon in your browser's address bar and allow notifications.
            </div>
          </CardContent>
        )}
        
        {isSubscribed && (
          <CardContent className="pt-0">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleTestNotification}
              disabled={testing}
            >
              {testing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Bell className="h-4 w-4 mr-2" />
                  Send Test Notification
                </>
              )}
            </Button>
          </CardContent>
        )}
      </Card>

      {/* Notification Types */}
      {isSubscribed && localPrefs && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Notification Types</CardTitle>
            <CardDescription>
              Choose which notifications you want to receive
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {NOTIFICATION_TYPES.map((type) => {
              const Icon = type.icon;
              const isEnabled = localPrefs.types?.[type.key] !== false;
              
              return (
                <div 
                  key={type.key} 
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${isEnabled ? 'bg-forest/10' : 'bg-gray-100'}`}>
                      <Icon className={`h-4 w-4 ${isEnabled ? 'text-forest' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{type.label}</p>
                      <p className="text-xs text-gray-500">{type.description}</p>
                    </div>
                  </div>
                  <Switch
                    checked={isEnabled}
                    onCheckedChange={(checked) => handleUpdatePreference(type.key, checked)}
                  />
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Quiet Hours */}
      {isSubscribed && localPrefs && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${localPrefs.quiet_hours?.enabled ? 'bg-purple-100' : 'bg-gray-100'}`}>
                  <Moon className={`h-5 w-5 ${localPrefs.quiet_hours?.enabled ? 'text-purple-600' : 'text-gray-400'}`} />
                </div>
                <div>
                  <CardTitle className="text-lg">Quiet Hours</CardTitle>
                  <CardDescription>
                    Pause notifications during specific hours
                  </CardDescription>
                </div>
              </div>
              <Switch
                checked={localPrefs.quiet_hours?.enabled || false}
                onCheckedChange={handleToggleQuietHours}
              />
            </div>
          </CardHeader>
          
          {localPrefs.quiet_hours?.enabled && (
            <CardContent className="pt-0">
              <div className="flex items-center gap-4 p-4 bg-purple-50/50 rounded-lg">
                <div className="flex-1">
                  <Label className="text-xs text-gray-500 mb-1 block">Start</Label>
                  <Select
                    value={String(localPrefs.quiet_hours?.start || 22)}
                    onValueChange={(v) => handleQuietHoursTime('start', v)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={String(i)}>
                          {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-gray-400 pt-5">to</div>
                <div className="flex-1">
                  <Label className="text-xs text-gray-500 mb-1 block">End</Label>
                  <Select
                    value={String(localPrefs.quiet_hours?.end || 7)}
                    onValueChange={(v) => handleQuietHoursTime('end', v)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={String(i)}>
                          {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                No notifications will be sent between these hours (based on your device time)
              </p>
            </CardContent>
          )}
        </Card>
      )}

      {/* Save Button */}
      {isSubscribed && localPrefs && (
        <div className="flex justify-end">
          <Button 
            onClick={handleSavePreferences}
            disabled={saving}
            className="btn-primary"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Preferences'
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default NotificationSettings;
